// "Unassign" in the open ticket's header: its owner (or an administrator) gives the ticket up.
// It loses its owner and leaves routing; it does not go back to Unassigned. The ticket stays
// open on screen and is refreshed, and the lists are refreshed so it leaves Assigned to me.

import { useCallback, useState } from 'react';
import axios from 'axios';
import { toast } from '@/components/ui/sonner';
import { invalidateQueries } from '@/lib/query-client';
import { sentinelWorkspaceService } from '@/services/sentinelWorkspace.service';

export interface UnassignTicket {
  unassign: (ticketId: number) => Promise<void>;
  /** The ticket being given up right now, so its button can show progress. */
  pendingTicketId: number | null;
}

export interface UnassignVisibilityInput {
  ownerId: number | null;
  currentUserId: string | number | null | undefined;
  /** An administrator (canManageSentinelMembersAtom). */
  isAdmin: boolean;
  isSentinel: boolean;
}

/** An owned ticket can be given up by its owner, or by an administrator who is not a Sentinel. */
export function canUnassignTicket({ ownerId, currentUserId, isAdmin, isSentinel }: UnassignVisibilityInput): boolean {
  if (ownerId === null) return false;
  if (isAdmin && !isSentinel) return true;
  return currentUserId != null && String(currentUserId) === String(ownerId);
}

function refreshLists(): void {
  void invalidateQueries(['sentinel-workspace', 'tickets']);
  void invalidateQueries(['sentinel', 'unassigned-counts']);
}

function failureMessage(error: unknown): string {
  const status = axios.isAxiosError(error) ? error.response?.status : undefined;
  if (status === 403) return 'Only the ticket’s owner can unassign it';
  if (status === 404) return 'This ticket no longer exists';
  if (status === 409) return 'This ticket is still being set up. Try again in a moment.';
  return 'Could not unassign the ticket. Please try again.';
}

export function useUnassignTicket(refreshTicket: () => void): UnassignTicket {
  const [pendingTicketId, setPendingTicketId] = useState<number | null>(null);

  const unassign = useCallback(
    async (ticketId: number) => {
      setPendingTicketId(ticketId);
      try {
        await sentinelWorkspaceService.unassign(ticketId);
        toast.success('Ticket unassigned');
        refreshTicket();
      } catch (error) {
        toast.error(failureMessage(error));
      } finally {
        refreshLists();
        setPendingTicketId(null);
      }
    },
    [refreshTicket],
  );

  return { unassign, pendingTicketId };
}
